import { useCallback, useRef } from "react";

import { Graph as Base } from "@corca/tiptap";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";

import { GRAPH_SCALE_CONTAINER_CLASS } from "@/components/graph/constants";
import { GraphZoomButtons } from "@/components/graph/GraphZoomButtons";
import { useGeogebraApplet } from "@/components/graph/useGeogebraApplet";
import { useGeogebraCommandRunner } from "@/components/graph/useGeogebraCommandRunner";
import { useUpdateGeogebraApplet } from "@/components/graph/useUpdateGeogebraApplet";
import { buildExpression, getXAxisBounds, useAxisVariables } from "@/components/graph/utils";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { getIsMobile } from "@/utils/getIsMobile";
import { twMerge } from "@/utils/twMerge";

import { BlockResizerControls, BlockResizerRoot } from "./BlockResizer";
import { useResponsiveImageStyle } from "./utils/useResponsiveImageStyle";

import type { GGBApi, GraphOptions } from "@/components/graph/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeViewProps } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    graph: {
      appendGraph: (position?: number, attrs?: object) => ReturnType;
    };
  }
}

type GraphNodeExtensionOptions = {
  doc: DocumentModel;
};

export type GraphNodeAttrs = GraphOptions & {
  graphId: string;
  svg?: string;
};

type GraphProps = {
  theme: "dark" | "light";
  hidden?: boolean;
  graphNode: GraphNodeModel;
  selected?: boolean;
  onUpdate?: (api: GGBApi) => void;
  options: GraphNodeAttrs;
} & React.HTMLAttributes<HTMLDivElement>;

const Graph = observer(function Graph({
  className,
  hidden,
  graphNode,
  onClick,
  selected,
  onUpdate,
  theme,
  options,
  ...rest
}: GraphProps) {
  const ref = useRef<HTMLDivElement>(null);
  const t = useTranslations("doc.graph");

  const { graphExpressionNodes, expressionColors } = graphNode;
  const expressions = graphExpressionNodes.map((n) => n.inlineNode!.nodes);
  const { axisVariables } = useAxisVariables(expressions);

  const xAxisBounds = getXAxisBounds(graphNode.doc, axisVariables.x);
  const { expression, rangedValueExpression } = buildExpression(
    expressions,
    axisVariables,
    xAxisBounds,
  );
  const { api } = useGeogebraApplet(ref, true, options, theme);

  const { evalStatus } = useGeogebraCommandRunner(
    {
      api,
      expression,
      rangedValueExpression,
      colors: expressionColors,
    },
    onUpdate,
  );

  useUpdateGeogebraApplet(
    api,
    {
      ...options,
      xAxisLabel: options.xAxisLabel ?? axisVariables.x,
      yAxisLabel: options.yAxisLabel ?? axisVariables.y,
    },
    onUpdate,
  );

  const wrapperStyle = useResponsiveImageStyle(options.width, options.height);

  const graph = (
    <figure
      ref={ref}
      onDragStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={twMerge(
        "corca-geogebra-graph",
        "absolute inset-0",
        evalStatus === "init" && "invisible",
      )}
      aria-label={t("figureLabel", { expression })}
      aria-busy={evalStatus === "init"}
    />
  );

  if (hidden)
    return (
      <div
        contentEditable={false}
        className={className}
        style={{
          width: options.width,
          height: options.height,
        }}
        {...rest}
      >
        {graph}
      </div>
    );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      contentEditable={false}
      className={twMerge(
        GRAPH_SCALE_CONTAINER_CLASS,
        "group/graph relative w-full overflow-hidden",
        className,
      )}
      onClick={onClick}
      style={{
        ...wrapperStyle,
        width: `min(100%, ${options.width}px)`,
        height: `min(100%, ${options.height}px)`,
      }}
      {...rest}
    >
      {evalStatus === "init" && (
        <div className="absolute inset-0 rounded-5 bg-gray-100 dark:bg-gray-900" />
      )}
      {graph}
      {api && (
        <GraphZoomButtons
          className={twMerge(
            "absolute bottom-1 right-1 opacity-0 transition-opacity group-hover/graph:opacity-100",
            "hidden fromSm:block",
            selected && "opacity-100",
          )}
          onZoomIn={() => api.evalCommand("ZoomIn(1.5)")}
          onZoomOut={() => api.evalCommand("ZoomOut(1.5)")}
        />
      )}
    </div>
  );
});

const GraphWrapper = observer(function GraphWrapper({
  node,
  extension,
  selected,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const t = useTranslations("doc.graph");
  const isMobile = getIsMobile();

  const { doc } = extension.options as GraphNodeExtensionOptions;
  const options = node.attrs as GraphNodeAttrs;

  const onClick = useCallback(() => {
    const pos = getPos();
    if (pos !== undefined) {
      doc.editor.tiptap?.chain().focus().setNodeSelection(pos).run();
    }
  }, [doc, getPos]);

  const { theme, systemTheme } = useTheme();
  const currentTheme = (theme === "system" ? systemTheme : (theme as "dark" | "light")) ?? "light";
  const isDark = currentTheme === "dark";

  const graphNode = doc.nodes.get(options.graphId);

  const onUpdate = useCallback(
    (api: GGBApi) => {
      api.exportSVG((svg) => {
        if (graphNode?.is(GraphNodeModel)) {
          if (graphNode.props.svg === svg) return;
          doc.transact(() => {
            graphNode.updateProps({ svg });
          }, "graph:update-svg");
        }
      });
    },
    [doc, graphNode],
  );

  if (!graphNode?.is(GraphNodeModel)) {
    return null;
  }

  return (
    <NodeViewWrapper>
      <LineWrapper doc={doc} getPos={getPos} contentClassName="flex">
        <BlockResizerRoot
          enabled={!isMobile && selected && doc.editable}
          className="w-full [&>button]:sm:hidden"
          onBlockResize={(size) => {
            updateAttributes({ width: size.width, height: size.height });
          }}
          style={{
            width: `min(100%, ${options.width}px)`,
            height: `min(100%, ${options.height}px)`,
          }}
        >
          <div className="w-0 h-0">&nbsp;</div>
          <BlockResizerControls
            getLabel={(index) => {
              return t(`resize-dot-label.${index}`, { index });
            }}
          />
          <Graph
            theme={currentTheme}
            graphNode={graphNode}
            selected={selected}
            onClick={onClick}
            onUpdate={isDark ? undefined : onUpdate}
            options={options}
          />
          {isDark && selected && (
            <div className="sr-only">
              <Graph
                aria-hidden
                theme="light"
                graphNode={graphNode}
                onUpdate={onUpdate}
                options={options}
              />
            </div>
          )}
        </BlockResizerRoot>
      </LineWrapper>
    </NodeViewWrapper>
  );
});

export const GraphPlugin = Base.extend<GraphNodeExtensionOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(GraphWrapper, {
      className: "w-full mx-auto max-w-content-width sm:max-w-full",
    });
  },
});
